/**
 * The lens dispensing job dialog.
 *
 * Six steps: frame -> prescription -> lens -> extras -> measurements -> order & billing. Picking a
 * frame keeps you on the frame step to check its size; picking a lens moves straight on. So the
 * happy path is: frame, Continue, Continue (the patient's current spectacle Rx already chosen),
 * lens, Continue, measure PD and heights, Create invoice & send.
 *
 * The prescription is not written here: it is chosen from the ones issued in the prescribing
 * module (LensPrescribingStore) and copied onto the job. Only what is taken with the frame in hand
 * - PD, fitting heights and the frame trace - is entered at dispensing, on the measurements step
 * after the extras.
 *
 * Frames are read from the practice's inventory and lenses from its lens catalogue. The job is saved
 * through LensDispensingStore: as a quote, then invoiced; the server takes the frame off stock once
 * the invoice is approved. Items added by hand (an eye examination, say) go on the same invoice.
 */
app.controller("lensDispensingModalController", [
	"$scope",
	"$mdDialog",
	"$timeout",
	"$animate",
	"$element",
	"$q",
	"LensDispensingStore",
	"LensPrescribingStore",
	"LensTraceParser",
	"InventoryAPI",
	"ExceptionAPI",
	"CurrentPatient",
	"Session",
	"SettingsDialog",
	"LensStatusDialog",
	"toast",
	"existingJob",
	"jobPatient",
	function ($scope, $mdDialog, $timeout, $animate, $element, $q, Store, Rx, TraceParser, InventoryAPI, ExceptionAPI, CurrentPatient, Session, SettingsDialog, LensStatusDialog, toast, existingJob, jobPatient) {
		$scope.S = Store;
		$scope.R = Rx;
		$scope.CP = CurrentPatient;

		// ngAnimate holds a leaving ng-if element (and any ng-class change) for the length of its CSS
		// transition, so the old step and its buttons linger beside the new one. Steps here switch
		// instantly and fade themselves in with a plain CSS animation instead.
		$animate.enabled(angular.element($element[0].querySelector(".lens-shell")), false);
		// The practice job list opens jobs for patients other than the current one, and says whose.
		const patient = jobPatient || CurrentPatient.patient || {};
		$scope.patient = patient;
		$scope.patientName = [patient.firstName, patient.lastName].filter(Boolean).join(" ") || "Patient";
		$scope.initials = ((patient.firstName || "P").charAt(0) + (patient.lastName || "").charAt(0)).toUpperCase();
		$scope.practiceName = (Session.practice && Session.practice.name) || "Your practice";

		$scope.steps = [
			{ key: "frame", label: "Frame", icon: "fa-square-o" },
			{ key: "rx", label: "Prescription", icon: "fa-eye" },
			{ key: "lens", label: "Lens", icon: "fa-circle-o" },
			{ key: "extras", label: "Extras", icon: "fa-magic" },
			{ key: "measure", label: "Measurements", icon: "fa-arrows-h" },
			{ key: "billing", label: "Order & billing", icon: "fa-credit-card" },
		];
		const BILLING = $scope.steps.findIndex((s) => s.key === "billing");
		const MEASURE = $scope.steps.findIndex((s) => s.key === "measure");
		$scope.isStep = (key) => $scope.steps[$scope.current].key === key;
		$scope.goMeasure = () => $scope.go(MEASURE);

		// A job made from a prescription arrives as an unsaved draft, so it is new though it is pre-filled.
		const isNew = !existingJob || existingJob.status === "DRAFT";
		$scope.job = existingJob ? angular.copy(existingJob) : Store.newJob("glasses");
		const job = $scope.job;

		const failMessage = (resp, fallback) => toast.create({ timeout: 6000, message: Store.errorMessage(resp, fallback), className: "alert-danger", dismissible: true });

		// The patient's prescriptions. A new job starts from their current spectacle Rx, if they have one.
		$scope.rxState = { loading: true, error: null };
		Rx.load(patient.id)
			.then(function () {
				const latestRx = Rx.usable(patient.id, "glasses")[0];
				if (isNew && !job.rxId && latestRx) {
					Rx.applyToJob(job, latestRx);
					$timeout(markClean);
				} else if (job.rxId && !$scope.locked && Rx.refreshRx(job, Rx.find(patient.id, job.rxId))) {
					// The job's copy had drifted from its prescription; the next save stores it again.
					$timeout(markClean);
				}
			})
			.catch((resp) => ($scope.rxState.error = Store.errorMessage(resp, "The patient's prescriptions could not be loaded.")))
			.finally(() => ($scope.rxState.loading = false));

		// The practice's dispensing extras and their prices. A new job starts with the coatings the
		// practice puts on every lens, which are only known once the settings are read.
		$scope.settingsState = { loading: true, error: null };
		Store.loadSettings()
			.then(function () {
				if (isNew && !job.extras.coatings.length && !$scope.dirty) {
					job.extras.coatings = Store.emptyExtras().coatings;
					$timeout(markClean);
				}
				refreshLines();
			})
			.catch((resp) => ($scope.settingsState.error = Store.errorMessage(resp, "The practice's dispensing extras could not be loaded.")))
			.finally(() => ($scope.settingsState.loading = false));

		// The practice's lens catalogue, for the lens step.
		$scope.catalogueState = { loading: true, error: null };
		Store.loadCatalogue()
			.catch((resp) => ($scope.catalogueState.error = Store.errorMessage(resp, "The lens catalogue could not be loaded.")))
			.finally(() => ($scope.catalogueState.loading = false));

		$scope.locked = Store.isLocked(job);
		$scope.visited = { frame: true };
		$scope.dirty = false;
		$timeout(markClean);

		// ------------------------------------------------------------------
		// Navigation
		// ------------------------------------------------------------------
		$scope.current = existingJob && (job.invoice || job.lens) ? BILLING : 0;
		if ($scope.current === BILLING) $scope.steps.forEach((s) => ($scope.visited[s.key] = true));

		$scope.go = function (i) {
			if (i < 0 || i >= $scope.steps.length) return;
			$scope.current = i;
			$scope.visited[$scope.steps[i].key] = true;
			if ($scope.steps[i].key === "lens") defaultDesignFilter();
			const body = $element[0].querySelector(".settings-pane-body");
			if (body) body.scrollTop = 0;
		};
		$scope.next = () => $scope.go($scope.current + 1);
		$scope.back = () => $scope.go($scope.current - 1);

		let advanceTimer = null;
		function autoAdvance() {
			$timeout.cancel(advanceTimer);
			advanceTimer = $timeout(() => $scope.next(), 380);
		}

		$scope.stepDone = function (key) {
			switch (key) {
				case "frame": return !!job.frame && (!job.frame.manual || !!(job.frame.model || "").trim());
				case "rx": return $scope.rxValid() && ($scope.locked || !$scope.rxExpired()) && !powersProblem("add");
				case "lens": return !!job.lens && !powersProblem("design");
				case "extras": return !!$scope.visited.measure || !!$scope.visited.billing || !!job.invoice;
				case "measure": return !$scope.missingMeasurements();
				case "billing": return !!job.invoice;
			}
			return false;
		};

		$scope.progress = function () {
			const done = $scope.steps.filter((s) => $scope.stepDone(s.key)).length;
			return Math.round((done / $scope.steps.length) * 100);
		};

		$scope.stepSummary = function (key) {
			switch (key) {
				case "frame": return job.frame ? Store.frameName(job.frame) || "Name the frame" : "Choose a frame";
				case "rx": return $scope.rxValid() ? `${Rx.useMeta(job.use).label} · R ${$scope.fmtSph(job.rx.od.sph)}  L ${$scope.fmtSph(job.rx.os.sph)}${hasAdd() ? "  Add " + $scope.fmtSph(job.rx.od.add || job.rx.os.add) : ""}` : "Choose a prescription";
				case "lens": return job.lens ? `${job.lens.supplier} ${job.lens.name}` : "Choose a lens";
				case "extras": {
					const n = Store.extrasLines(job.extras).filter((l) => l.price > 0).length;
					return n ? `${n} paid extra${n > 1 ? "s" : ""}` : "Tint, coatings, add-ons";
				}
				case "measure": {
					if ($scope.missingMeasurements()) return "Measure PD & heights";
					return `PD R ${job.rx.od.pd} / L ${job.rx.os.pd} · heights ${job.rx.od.height} / ${job.rx.os.height}${job.trace ? " · traced" : ""}`;
				}
				case "billing": return job.invoice ? "Invoice sent" : "Invoice & order";
			}
		};

		$scope.stepPrice = function (key) {
			const t = $scope.totals();
			if (key === "frame") return job.frame ? t.framePrice : null;
			if (key === "lens") return job.lens ? t.lensPrice : null;
			if (key === "extras") return t.extrasPrice || null;
			return null;
		};

		$scope.totals = () => Store.totals(job);

		// Changed since it was opened or last saved, against a snapshot - so a default filled in while
		// loading, or an edit put back, doesn't count as a change.
		let cleanJob = null;
		function markClean() {
			cleanJob = angular.toJson(job);
			$scope.dirty = false;
		}
		$scope.$watch("job", () => ($scope.dirty = cleanJob !== null && angular.toJson(job) !== cleanJob), true);

		// ------------------------------------------------------------------
		// Step 1 - frame
		// ------------------------------------------------------------------
		// The practice's FRAME inventory, 25 at a time: the whole category, or what the search box
		// finds on the server. Neither list carries variations, so each item is fetched again for its
		// colours, sizes and stock; an item that fails to load is left off rather than holding up the page.
		const FRAME_PAGE_SIZE = 25;
		const inventoryOn = !!(Session.practice && Session.practice.practiceFeatures && Session.practice.practiceFeatures.inventoryFeature);
		$scope.inventoryOn = inventoryOn;
		$scope.frameFilter = { shape: "All", text: "" };

		// All digits is a scanned barcode, which the name search would not find. A search covers
		// every category, so frames are picked out after the page is counted.
		function fetchFramePage(term, pageIndexes) {
			if (/^\d{6,}$/.test(term)) {
				return InventoryAPI.getItemsByBarcode(term).then((resp) => ({ items: resp.data, page_indexes: [] }));
			}
			const request = term
				? InventoryAPI.searchItems(term, pageIndexes)
				: InventoryAPI.getItemsByCategory(InventoryAPI.CATEGORY.FRAME, null, pageIndexes);
			return request.then((resp) => resp.data || {});
		}

		function loadFramePage() {
			const stock = $scope.frameStock;
			const frames = $scope.frames;
			if (stock.loading) return;
			stock.loading = true;
			stock.error = null;
			fetchFramePage(stock.term, stock.pageIndexes)
				.then(function (data) {
					// A new search replaced this list while the page was on its way.
					if (stock !== $scope.frameStock) return [];
					const items = Array.isArray(data.items) ? data.items : [];
					// A short page is the last one, even if the server still sends page_indexes.
					const lastPage = items.length < FRAME_PAGE_SIZE || !Array.isArray(data.page_indexes);
					stock.pageIndexes = lastPage ? [] : data.page_indexes;
					const frameItems = items.filter((item) => item.category === InventoryAPI.CATEGORY.FRAME);
					return $q.all(frameItems.map((item) => InventoryAPI.getItem(item.item_id).then((r) => r.data, () => null)));
				})
				.then(function (items) {
					items.filter((item) => InventoryAPI.activeVariations(item).length).forEach(function (item) {
						const frame = Store.frameFromInventory(item);
						const chosen = job.frame && job.frame.id === frame.id && job.frame.colour;
						frame.$colour = (chosen && frame.colours.find((c) => c.variationId === chosen.variationId)) || frame.colours[0];
						frames.push(frame);
					});
					stock.pagesLoaded++;
				})
				.catch(function (resp) {
					stock.error = ExceptionAPI.serverMessage(resp) || "Could not load frames from inventory.";
				})
				.finally(function () {
					stock.loading = false;
				});
		}

		function listFrames(term) {
			$scope.frames = [];
			$scope.frameStock = { loading: false, error: null, pageIndexes: [], pagesLoaded: 0, term: term };
			if (inventoryOn) loadFramePage();
		}
		$scope.loadMoreFrames = loadFramePage;
		$scope.moreFrames = () => $scope.frameStock.pageIndexes.length > 0;
		// Without inventory, or while it can't be read, the frame is entered by hand - a job never waits on it.
		$scope.offerOtherFrame = () => !inventoryOn || !!$scope.frameStock.error || (!!job.frame && !!job.frame.manual);
		listFrames("");

		// The server search needs 2 characters; fewer lists the whole category.
		$scope.$watch("frameFilter.text", function (text, old) {
			if (text === old || !inventoryOn) return;
			const term = (text || "").trim();
			const searchTerm = term.length >= 2 ? term : "";
			if (searchTerm !== $scope.frameStock.term) listFrames(searchTerm);
		});

		$scope.shapes = ["All", "round", "oval", "rectangle", "square", "cateye", "aviator", "browline", "rimless"];
		$scope.shapeLabel = (s) => ({ cateye: "Cat-eye", All: "All shapes" }[s] || s.charAt(0).toUpperCase() + s.slice(1));

		// Shape is guessed from the name here rather than stored, so it is filtered here too.
		$scope.visibleFrames = function () {
			const shape = $scope.frameFilter.shape;
			return $scope.frames.filter((fr) => shape === "All" || fr.shape === shape);
		};

		$scope.frameMeta = (frame) => [frame.$colour.size, frame.material, frame.$colour.location].filter(Boolean).join(" · ");

		$scope.pickColour = function (frame, colour, $event) {
			if ($event) $event.stopPropagation();
			if ($scope.locked) return;
			frame.$colour = colour;
			if (job.frame && job.frame.id === frame.id) $scope.chooseFrameColour(colour);
		};

		// Glaze new lenses into the frame the patient already has. Its description, size and any
		// charge for fitting the lenses are on the card above the frames.
		$scope.useOwnFrame = function () {
			if ($scope.locked) return;
			if (!(job.frame && job.frame.own)) {
				job.trace = null;
				job.frame = Store.ownFrame();
			}
			const body = $element[0].querySelector(".settings-pane-body");
			if (body) body.scrollTop = 0;
		};

		// A frame the practice sells that isn't in inventory: its name, colour and price are on the card above.
		$scope.useOtherFrame = function () {
			if ($scope.locked) return;
			if (!(job.frame && job.frame.manual)) {
				job.trace = null;
				job.frame = Store.otherFrame();
			}
			const body = $element[0].querySelector(".settings-pane-body");
			if (body) body.scrollTop = 0;
		};

		$scope.selectFrame = function (frame) {
			if ($scope.locked) return;
			const copy = angular.copy(frame);
			const colour = copy.colours[frame.colours.indexOf(frame.$colour)];
			delete copy.$colour;
			// A trace belongs to the frame it was taken from - the same model in another size is not.
			const sameFrame = job.frame && job.frame.id === copy.id && (!copy.fromInventory || job.frame.size === colour.size);
			if (!sameFrame) job.trace = null;
			Store.setFrameColour(copy, colour);
			job.frame = copy;
			// Stay on the frame step - its size is on the card above the frames.
			const body = $element[0].querySelector(".settings-pane-body");
			if (body) body.scrollTop = 0;
		};

		// The chosen frame's size - eye, bridge (DBL) and temple, in mm - kept on the frame as
		// "52-18-140". An inventory frame brings its variation's size; it can be put right here.
		$scope.frameSize = { eye: null, dbl: null, temple: null };
		const toNum = (v) => (v === null || v === undefined || v === "" || isNaN(v) ? null : Number(v));
		const sizeParts = (size) => {
			const m = /^\s*(\d+(?:\.\d+)?)?\D+(\d+(?:\.\d+)?)?\D+(\d+(?:\.\d+)?)?\s*$/.exec(size || "");
			return m ? { eye: toNum(m[1]), dbl: toNum(m[2]), temple: toNum(m[3]) } : { eye: null, dbl: null, temple: null };
		};
		const sizeText = (s) => (isNum(s.eye) || isNum(s.dbl) || isNum(s.temple) ? [s.eye, s.dbl, s.temple].map((v) => (isNum(v) ? v : "?")).join("-") : "");
		$scope.$watch("job.frame.size", function (size) {
			if (size !== sizeText($scope.frameSize)) $scope.frameSize = sizeParts(size);
		});
		$scope.onFrameSizeChange = function () {
			if (job.frame && !$scope.locked) job.frame.size = sizeText($scope.frameSize);
		};

		// A different colour of the chosen frame. For an inventory frame that is another variation,
		// which may be another size, and then the trace no longer fits it.
		$scope.chooseFrameColour = function (colour) {
			if ($scope.locked || !job.frame) return;
			const size = job.frame.size;
			Store.setFrameColour(job.frame, colour);
			if (job.frame.size !== size) $scope.clearTrace();
		};

		// ------------------------------------------------------------------
		// Frame trace: the box measurements read off the workshop tracer, either typed in or imported
		// from the tracer's OMA/VCA export file. They go on the lab order, so the lab can edge without
		// the frame; an imported trace keeps the file, and the order draws the lens shape from it.
		// ------------------------------------------------------------------
		$scope.traceEdit = null;
		$scope.traceAccept = TraceParser.ACCEPT;
		// The file last imported, and why it couldn't be used.
		$scope.traceImport = { fileName: null, error: null };

		$scope.editTrace = function () {
			if ($scope.locked || !job.frame) return;
			// A new trace starts from the frame's size: its eye size is box A, and its bridge the DBL.
			const size = $scope.frameSize;
			$scope.traceEdit = job.trace ? angular.copy(job.trace) : { a: toNum(size.eye), b: null, dbl: toNum(size.dbl), ed: null, frameCurve: null };
			$scope.traceImport = { fileName: null, error: null };
		};

		const positive = (v) => v !== null && v !== undefined && v !== "" && Number(v) > 0;
		$scope.traceValid = () => !!$scope.traceEdit && positive($scope.traceEdit.a) && positive($scope.traceEdit.b) && positive($scope.traceEdit.dbl);

		$scope.saveTrace = function () {
			if (!$scope.traceValid()) return;
			// An imported trace keeps the tracer's details and when it was imported; one typed in says so.
			const typed = { device: "Manual entry", mode: "Frame", format: null, outline: null, tracedAt: new Date() };
			job.trace = $scope.traceEdit.outline ? angular.extend($scope.traceEdit, { tracedAt: $scope.traceEdit.tracedAt || new Date() }) : angular.extend($scope.traceEdit, typed);
			$scope.traceEdit = null;
			$scope.traceImport = { fileName: null, error: null };
		};

		$scope.cancelTrace = function () {
			$scope.traceEdit = null;
			$scope.traceImport = { fileName: null, error: null };
		};

		$scope.clearTrace = function () {
			if ($scope.locked) return;
			job.trace = null;
			$scope.traceEdit = null;
			$scope.traceImport = { fileName: null, error: null };
		};

		// Import the tracer's export file. Its figures open in the trace form to check before saving,
		// with the outline drawn beside them. Called from the file input's onchange, outside Angular.
		$scope.importTraceFile = function (input) {
			const file = input && input.files && input.files[0];
			// Clear the input so choosing the same file again (after fixing it, say) still imports it.
			if (input) input.value = "";
			if (!file) return;
			$scope.$applyAsync(function () {
				if ($scope.locked || !job.frame) return;
				if (file.size > TraceParser.MAX_BYTES) {
					$scope.traceImport = { fileName: file.name, error: "That file is too large for a frame trace (over 200 KB). Choose the OMA/VCA file the tracer exported." };
					return;
				}
				const reader = new FileReader();
				reader.onload = () => $scope.$applyAsync(() => useTraceText(reader.result, file.name));
				reader.onerror = () => $scope.$applyAsync(() => ($scope.traceImport = { fileName: file.name, error: "The file could not be read. Try exporting it from the tracer again." }));
				reader.readAsText(file);
			});
		};

		// A file dropped on the card is imported as if it had been chosen.
		$scope.traceDrag = { over: false };
		$scope.traceDragOver = function (over) {
			if ($scope.traceDrag.over !== over) $scope.$applyAsync(() => ($scope.traceDrag.over = over));
		};
		$scope.dropTraceFile = function (event) {
			$scope.traceDragOver(false);
			const files = event && event.dataTransfer && event.dataTransfer.files;
			if (files && files.length) $scope.importTraceFile({ files: files });
		};

		function useTraceText(text, fileName) {
			const parsed = TraceParser.parse(text);
			if (parsed.error) {
				$scope.traceImport = { fileName: fileName, error: parsed.error };
				return;
			}
			$scope.traceImport = { fileName: fileName, error: null };
			$scope.traceEdit = parsed.trace;
		}

		// The outline drawn on the card, for the saved trace and the one being checked. Worked out only
		// when the outline or the DBL it is spaced by changes.
		$scope.tracePreview = { saved: null, edit: null };
		$scope.$watchGroup(["job.trace.outline", "job.trace.dbl"], (v) => ($scope.tracePreview.saved = TraceParser.preview(v[0], v[1])));
		$scope.$watchGroup(["traceEdit.outline", "traceEdit.dbl"], (v) => ($scope.tracePreview.edit = TraceParser.preview(v[0], v[1])));

		// The order PDF, saved to the patient's files when the order was sent.
		$scope.hasOrderDocument = () => Store.hasOrderDocument(job);
		$scope.viewOrderDocument = function () {
			Store.orderDocument(patient.id, job).then(
				function (blob) {
					const url = URL.createObjectURL(blob);
					window.open(url, "_blank");
					// The new tab has loaded it well before this; the URL only has to outlive that.
					setTimeout(() => URL.revokeObjectURL(url), 60000);
				},
				(resp) => failMessage(resp, "The order PDF could not be opened.")
			);
		};

		// ------------------------------------------------------------------
		// Step 2 - prescription (chosen, not written) and the frame measurements
		// ------------------------------------------------------------------
		$scope.eyes = [{ key: "od", label: "Right", sub: "OD" }, { key: "os", label: "Left", sub: "OS" }];

		const isNum = (v) => v !== null && v !== undefined && v !== "" && !isNaN(v);
		const hasAdd = () => Number(job.rx.od.add) > 0 || Number(job.rx.os.add) > 0;

		// The patient's spectacle prescriptions that are issued and in date, newest first.
		$scope.prescriptions = () => Rx.usable(patient.id, "glasses");
		$scope.prescription = () => Rx.find(patient.id, job.rxId);
		$scope.rxStatus = (p) => Rx.statusOf(patient.id, p);

		$scope.choosePrescription = function (p) {
			if ($scope.locked) return;
			Rx.applyToJob(job, p);
		};

		// Dispensing on an expired Rx needs a new one - only a job not yet sent can still change.
		$scope.rxExpired = function () {
			if (!job.rx.expires) return false;
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			return new Date(job.rx.expires) < today;
		};

		$scope.fmtSph = function (v) {
			if (!isNum(v)) return "-";
			const n = Number(v);
			return (n > 0 ? "+" : n < 0 ? "" : "") + n.toFixed(2).replace(/^-?0\.00$/, "0.00");
		};
		$scope.fmtEye = function (e) {
			if (e && e.balance) return "Balance";
			if (!e || !isNum(e.sph)) return "-";
			let s = $scope.fmtSph(e.sph);
			if (isNum(e.cyl) && Number(e.cyl) !== 0) s += ` / ${$scope.fmtSph(e.cyl)} × ${e.axis || "?"}`;
			else s += " DS";
			if (Number(e.intAdd) > 0) s += `  Int ${$scope.fmtSph(e.intAdd)}`;
			if (Number(e.add) > 0) s += `  Add ${$scope.fmtSph(e.add)}`;
			const prism = Store.fmtPrism(e);
			if (prism) s += `  Prism ${prism}`;
			return s;
		};
		$scope.fmtPrism = (e) => Store.fmtPrism(e) || "-";
		$scope.fmtMeasurements = (e) => Store.fmtMeasurements(e) || "Not measured";

		// What the lab needs to centre the lenses that hasn't been measured in the frame yet.
		$scope.missingMeasurements = function () {
			const missing = [];
			if (["od", "os"].some((k) => !isNum(job.rx[k].pd))) missing.push("PD");
			if (["od", "os"].some((k) => !isNum(job.rx[k].height))) missing.push("fitting heights");
			return missing.join(" and ");
		};

		$scope.rxValid = function () {
			return ["od", "os"].every((k) => {
				const e = job.rx[k];
				if (e.balance) return true;
				if (!isNum(e.sph)) return false;
				if (isNum(e.cyl) && Number(e.cyl) !== 0 && !(Number(e.axis) >= 1 && Number(e.axis) <= 180)) return false;
				return true;
			});
		};


		// ------------------------------------------------------------------
		// Step 3 - lens
		// ------------------------------------------------------------------
		$scope.lensFilter = { design: "All" };

		const suggestedDesign = () => Rx.useMeta(job.use).design;
		$scope.suggestedDesign = suggestedDesign;

		function defaultDesignFilter() {
			if (!job.lens) $scope.lensFilter.design = suggestedDesign();
		}

		// The highest power the lenses are made to, through the strongest meridian - and through the near
		// zone (sphere + ADD) on a lens that has one, which is where a plus lens is thickest.
		$scope.maxPower = function () {
			const made = $scope.powers && $scope.powers.eyes ? $scope.powers.eyes : job.rx;
			const zoned = !!($scope.powers && $scope.powers.zoned);
			return Math.max(...["od", "os"].map((k) => {
				// Through the minus-cylinder form, so a plus-cylinder Rx is measured on its strongest meridian too.
				const e = Store.toMinusCyl(made[k] || {});
				const sph = Number(e.sph) || 0;
				const cyl = Math.min(Number(e.cyl) || 0, 0);
				const near = zoned ? sph + (Number(e.add) || 0) : sph;
				return Math.max(Math.abs(sph + cyl), Math.abs(near + cyl), Math.abs(sph), Math.abs(near));
			}));
		};

		$scope.recommendedIndex = function () {
			const p = $scope.maxPower();
			if (p >= 6) return 1.74;
			if (p >= 4) return 1.67;
			if (p >= 2) return 1.6;
			return 1.5;
		};

		// Whether a lens design can be made from this Rx for this use - one with zones needs an ADD, and
		// "all distances" needs zones. Kept with the powers; the lens cards and filter read it.
		$scope.designFit = {};
		$scope.hasAdd = () => hasAdd();
		$scope.unsuitable = (design) => !!($scope.designFit[design] && $scope.designFit[design].problem);

		$scope.visibleLenses = function () {
			return Store.lenses().filter((l) => $scope.lensFilter.design === "All" || l.design === $scope.lensFilter.design);
		};

		// The cheapest lens of the suggested design that is at least as thin as the Rx calls for.
		$scope.recommendedLens = function () {
			const want = $scope.recommendedIndex();
			const candidates = Store.lenses().filter((l) => l.design === suggestedDesign() && !$scope.unsuitable(l.design) && l.index >= want).sort((a, b) => a.price - b.price);
			return candidates[0] || null;
		};

		$scope.designLabel = (key) => (Store.LENS_DESIGNS.find((d) => d.key === key) || {}).label || key;

		// What the glasses are for. It starts as prescribed; choosing another use suggests that use's
		// lens design instead. The lens already chosen stays until another is picked. A use the
		// prescription can't give a power for can't be chosen.
		$scope.prescribedUse = () => (job.rxId ? job.rx.use : null);

		$scope.setUse = function (key) {
			const option = $scope.useOptions.find((o) => o.use.key === key);
			if ($scope.locked || job.use === key || (option && option.blocked)) return;
			job.use = key;
			$scope.lensFilter.design = suggestedDesign();
		};

		// What the lab makes for the use and lens, worked out from the prescription - and what each use
		// would make, shown on its choice. Kept between changes to the job: the templates repeat them.
		$scope.powers = null;
		$scope.useOptions = [];
		const designFor = (use) => (job.lens ? job.lens.design : Rx.useMeta(use).design);

		// The lens already chosen is of another design than this use calls for - its powers follow the lens.
		$scope.lensMismatch = () => !!job.lens && !!job.use && job.lens.design !== suggestedDesign();
		const powersProblem = (kind) => !!($scope.powers && $scope.powers.problem && $scope.powers.problem.kind === kind);

		function refreshPowers() {
			if (!$scope.rxValid()) {
				$scope.powers = null;
				$scope.useOptions = [];
				$scope.designFit = {};
				return;
			}
			$scope.powers = Store.lensPowers(job, designFor(job.use));
			$scope.designFit = {};
			Store.LENS_DESIGNS.forEach(function (d) {
				const p = Store.lensPowers(job, d.key);
				const problem = p.problem && p.problem.kind === "design" ? p.problem.text : null;
				$scope.designFit[d.key] = {
					problem: problem,
					note: problem ? (p.zoned ? "Needs an ADD" : "One power only") : p.zoned && hasAdd() ? `Uses ADD ${$scope.fmtSph(job.rx.od.add || job.rx.os.add)}` : null,
				};
			});
			// Each choice says what it gives with the lens design suggested for it; one the prescription
			// has no power for, whatever the lens, can't be chosen.
			$scope.useOptions = Rx.USES.map(function (u) {
				const asUse = angular.extend({}, job, { use: u.key });
				const suggested = Store.lensPowers(asUse, Rx.useMeta(u.key).design);
				const single = Store.lensPowers(asUse, "single");
				const blocked = single.problem && single.problem.kind === "add" ? single.problem.text : null;
				return { use: u, effect: blocked ? "Needs an ADD" : suggested.short, blocked: blocked };
			});
		}

		$scope.selectLens = function (lens) {
			if ($scope.locked || $scope.unsuitable(lens.design)) return;
			job.lens = angular.copy(lens);
			autoAdvance();
		};

		// ------------------------------------------------------------------
		// Step 4 - extras
		// ------------------------------------------------------------------
		$scope.toggleIn = function (list, id) {
			if ($scope.locked) return;
			const i = list.indexOf(id);
			if (i >= 0) list.splice(i, 1);
			else list.push(id);
		};

		$scope.setExtra = function (key, value) {
			if ($scope.locked) return;
			job.extras[key] = value;
		};

		$scope.previewTint = function () {
			const tint = Store.extra("tints", job.extras.tint);
			if (tint && tint.hex) return tint.hex;
			const type = Store.extra("lensTypes", job.extras.lensType);
			return type && type.previewHex ? type.previewHex : null;
		};

		$scope.previewOpacity = function () {
			const tint = Store.extra("tints", job.extras.tint);
			if (tint && tint.hex) return job.extras.density;
			return job.extras.lensType === "polarised" ? 75 : 45;
		};

		$scope.previewMirror = function () {
			const m = Store.extra("mirrors", job.extras.mirror);
			return m ? m.hex : null;
		};

		// Built fresh each time, so cache them per change of the job - ng-repeat over a function that
		// returns new objects every digest never settles.
		let cachedExtras = [];
		$scope.extrasLines = () => cachedExtras;

		// ------------------------------------------------------------------
		// Step 5 - order & billing
		// ------------------------------------------------------------------
		$scope.paymentOptions = [
			{ key: "deposit", label: "50% deposit now", icon: "fa-adjust" },
			{ key: "full", label: "Pay in full now", icon: "fa-check-circle" },
			{ key: "collection", label: "Pay on collection", icon: "fa-clock-o" },
		];

		$scope.eta = function () {
			return Store.addDays(job.created, (job.lens ? job.lens.leadDays : 5) + 2);
		};
		$scope.lab = () => Store.labFor(job.lens);

		// Items added by hand to go on this invoice - an eye examination with the glasses, say - so
		// the visit doesn't need a second invoice. The name searches the practice's Xero billing items
		// and its inventory, like the invoice dialog; a new name can also just be typed.
		$scope.newItem = { name: "", price: null, code: null, pickedName: null };
		$scope.searchItems = (term) => Store.searchBillingItems(term);
		$scope.itemLabel = Store.billingItemLabel;
		$scope.pickItem = function (item) {
			$scope.newItem.name = item.name;
			if (isNum(item.unit_price)) $scope.newItem.price = Number(item.unit_price);
			$scope.newItem.code = item.code || null;
			$scope.newItem.pickedName = item.name;
		};
		$scope.canAddItems = () => !$scope.locked && !job.invoice;
		$scope.addCustomItem = function () {
			const name = ($scope.newItem.name || "").trim();
			if (!$scope.canAddItems() || !name || !(Number($scope.newItem.price) >= 0)) return;
			if (!job.customItems) job.customItems = [];
			// A picked item keeps its Xero code, so the invoice bills the existing item - unless the
			// name was changed after picking, which makes it a new one.
			const code = name === $scope.newItem.pickedName ? $scope.newItem.code : null;
			job.customItems.push({ name: name, price: Number($scope.newItem.price), code: code });
			$scope.newItem = { name: "", price: null, code: null, pickedName: null };
		};
		$scope.removeCustomItem = function (index) {
			if (!$scope.canAddItems()) return;
			job.customItems.splice(index, 1);
		};

		// Once invoiced, the lines are the invoice's own; before, they are priced from the settings.
		let cachedBilling = [];
		$scope.billingLines = () => cachedBilling;
		// The practice's extras and their prices are set in practice settings, opened over this dialog.
		// Saving there updates what the store offers, so the job's lines are worked out again after.
		$scope.canEditExtras = Session.hasPermission("PRACTICE_LENS_CATALOGUE_MANAGE");
		$scope.editExtras = function () {
			SettingsDialog.openSettings("lensDispensing", { multiple: true }).finally(refreshLines);
		};

		function refreshLines() {
			cachedExtras = Store.extrasLines(job.extras);
			cachedBilling = Store.jobLines(job);
			refreshPowers();
		}

		$scope.$watch("job", refreshLines, true);

		$scope.missing = function () {
			return $scope.steps.slice(0, 3).filter((s) => !$scope.stepDone(s.key));
		};

		// What stops the invoice being created, said on the action bar as the one thing to do - null once
		// nothing does. A string, so the template can watch it.
		const XERO_NEEDS = {
			checking: "Checking Xero\u2026",
			login: "Log in to Xero first",
			contact: "Choose the patient's Xero contact",
			archived: "Restore the Xero contact first",
			conflict: "Choose the Xero organisation",
			unreachable: "Xero unreachable - retry",
		};
		$scope.invoiceBlocker = function () {
			const missing = $scope.missing();
			if (missing.length) return `Finish ${missing[0].label.toLowerCase()} first`;
			if ($scope.xero.enabled && !$scope.xero.ready && !$scope.xero.skipped) return XERO_NEEDS[$scope.xero.step] || XERO_NEEDS.checking;
			return null;
		};

		// Takes the user to it: the unfinished step, or the Xero panel (its contact search, if that's what's needed).
		$scope.fixBlocker = function () {
			const missing = $scope.missing();
			if (missing.length) return $scope.go($scope.steps.indexOf(missing[0]));
			const panel = $element[0].querySelector(".lens-xero");
			if (!panel) return;
			panel.scrollIntoView({ behavior: "smooth", block: "center" });
			const search = panel.querySelector(".lens-xero-search input");
			if (search) search.focus();
		};

		$scope.canInvoice = () => !$scope.invoiceBlocker() && !$scope.sending;

		// A practice billing through Xero raises the invoice there; lens-xero-invoicing checks the login
		// and the patient's contact, and says when both are in place - or that the user chose to invoice
		// without Xero, kept in CatTrax to raise in Xero later.
		const practiceFeatures = (Session.practice && Session.practice.practiceFeatures) || {};
		$scope.xero = { enabled: !!practiceFeatures.billingFeature, ready: false };

		$scope.sending = false;
		$scope.saving = false;

		// The server's copy replaces the dialog's, keeping the object the template is bound to.
		function adopt(saved) {
			angular.copy(saved, job);
			$scope.locked = Store.isLocked(job);
			$timeout(markClean);
			return job;
		}

		// The invoice is created - in Xero, approved, where the practice bills there - and the dialog stays
		// open for the payment.
		$scope.createInvoice = function () {
			if (!$scope.canInvoice()) return;
			$scope.sending = true;
			// The patient's Xero contact is linked, or made, as the invoice is raised.
			const keepOutOfXero = $scope.xero.enabled && !!$scope.xero.skipped;
			const contactReady = $scope.xero.enabled && !keepOutOfXero && $scope.xero.prepare ? $scope.xero.prepare() : null;
			const invoice = () => Store.invoiceJob(patient.id, job, null, false, keepOutOfXero);
			(contactReady ? contactReady.then(invoice) : invoice())
				.then(function (saved) {
					if (saved.$orderPdfFailed) {
						delete saved.$orderPdfFailed;
						failMessage(null, "The job is invoiced, but its order PDF couldn't be made. Marking the order sent makes it again.");
					}
					adopt(saved);
					const notice = Store.invoicedNotice(saved, $scope.patientName, false);
					if (notice) toast.create({ timeout: 6000, message: notice.message, className: notice.className, dismissible: true });
				})
				.catch(function (resp) {
					// Logged out of Xero since the check: the panel goes back to its login step.
					if (resp && resp.status === 401 && $scope.xero.check) $scope.xero.check();
					failMessage(resp, "The job could not be invoiced.");
				})
				.finally(() => ($scope.sending = false));
		};

		// A job kept in CatTrax, raised in Xero from its invoice card.
		$scope.raisedInXero = (saved) => adopt(saved);

		// Paid: the dialog's work is done.
		$scope.paid = function (saved) {
			$mdDialog.hide(saved);
		};

		// Closing an invoiced job without (more) payment. It is locked, so there is nothing to save.
		$scope.finishInvoiced = function () {
			$mdDialog.hide(job);
		};

		$scope.canSaveQuote = () => !!job.frame && !!job.rxId && !$scope.saving;

		$scope.saveQuote = function () {
			if (!$scope.canSaveQuote()) return;
			$scope.saving = true;
			Store.saveJob(patient.id, job)
				.then((saved) => $mdDialog.hide(saved))
				.catch((resp) => failMessage(resp, "The quote could not be saved."))
				.finally(() => ($scope.saving = false));
		};


		// Marking the order sent from here once the job is invoiced - the same step as Mark order sent on the job
		// tile: it makes the order PDF and saves it to the patient's documents. Moving the job on isn't an edit,
		// so the lock doesn't stop it.
		$scope.canSendOrder = () => !!job.id && (Store.nextActionFor(job) || {}).next === "ORDERED";
		$scope.sendingOrder = false;
		$scope.sendOrder = function () {
			if (!$scope.canSendOrder() || $scope.sendingOrder) return;
			const action = Store.nextActionFor(job);
			const sentTo = $scope.lab();
			$scope.sendingOrder = true;
			LensStatusDialog.show(job, patient, action, true)
				.then(
					(result) => Store.advanceJob(patient.id, result.job || job, { reference: result.reference || null }).then(function (sent) {
						adopt(sent);
						toast.create({
							timeout: 6000, className: "alert-success", dismissible: true,
							message: `Order marked sent to ${job.order.lab || sentTo}. The order PDF is in ${patient.firstName || "the patient"}'s documents.`,
						});
					}),
					// Cancelled in the dialog.
					() => null
				)
				.catch((resp) => failMessage(resp, "The order could not be sent."))
				.finally(() => ($scope.sendingOrder = false));
		};

		// ------------------------------------------------------------------
		// Close
		// ------------------------------------------------------------------
		$scope.confirmClose = false;
		// A function, not "confirmClose = false" in the template: the prompt sits in an ng-if, whose
		// child scope would get its own confirmClose and leave this one - and the prompt - as they were.
		$scope.keepEditing = () => ($scope.confirmClose = false);
		$scope.close = function () {
			if ($scope.dirty && job.frame && !$scope.locked && !job.invoice) {
				$scope.confirmClose = true;
				return;
			}
			$mdDialog.cancel();
		};
		$scope.discard = () => $mdDialog.cancel();
	},
]);
